import {
  MAINTENANCE_BODY,
  MAINTENANCE_TITLE,
  SERVICE_UNAVAILABLE_BODY,
  SERVICE_UNAVAILABLE_TITLE,
  STAGE_MISMATCH_BODY,
  STAGE_MISMATCH_TITLE,
} from '@kenocyber/shared';
import type { ServiceState } from '../service/availability';

/**
 * The whole screen, when the game is not being served.
 *
 * ## It REPLACES everything, it does not cover it
 *
 * Rendered instead of the session boundary, so no canvas exists, no simulation
 * ticks, no checkpoint is attempted and no sign-in form is on screen. A notice
 * drawn over a running arena would look identical and be the opposite: a client
 * quietly minting progress against a wallet a reset is rewriting.
 *
 * ## Three states, three sets of words
 *
 * The wording is the SHARED one, so the sentence a player reads is the sentence
 * this codebase agrees on rather than a second copy that drifts. Only the
 * accent colour and the title change between them - the shape is the same,
 * because it is the same event from the player's side: the game is not
 * available and here is the honest reason.
 *
 * Nothing here names a stage, a version, a run, a hash or a count. A player
 * learns that something is happening; which one is an internal fact.
 *
 * ## No promise it cannot keep
 *
 * No countdown, no progress bar, no estimate. Neither the client nor the server
 * knows when a transition finishes. The one number on screen is when the last
 * check happened, from the browser's own clock, presented as exactly that.
 */

const COPY: Record<
  Exclude<ServiceState, 'serving'>,
  { readonly title: string; readonly body: readonly string[]; readonly tone: string }
> = {
  maintenance: { title: MAINTENANCE_TITLE, body: MAINTENANCE_BODY, tone: 'maintenance' },
  stage_mismatch: { title: STAGE_MISMATCH_TITLE, body: STAGE_MISMATCH_BODY, tone: 'update' },
  unreachable: {
    title: SERVICE_UNAVAILABLE_TITLE,
    body: SERVICE_UNAVAILABLE_BODY,
    tone: 'offline',
  },
};

export interface ServiceScreenProps {
  readonly state: Exclude<ServiceState, 'serving'>;
  readonly checking: boolean;
  readonly checkedAt: Date | null;
  readonly onRecheck: () => void;
}

/** `14:03:27`, from the browser's clock. Information, never authority. */
function clockLabel(at: Date): string {
  return at.toLocaleTimeString('pt-BR', { hour12: false });
}

export function ServiceScreen({ state, checking, checkedAt, onRecheck }: ServiceScreenProps) {
  const copy = COPY[state];
  return (
    <section
      className={`service-screen service-screen--${copy.tone}`}
      data-testid="service-screen"
      data-state={state}
      role="status"
      aria-live="polite"
    >
      <div className="service-screen__panel">
        <p className="service-screen__brand">KENOCYBER</p>
        {/* Three dots, and that is the entire animation. It says the page is
            alive; it does not pretend to measure anything. */}
        <div className="service-screen__pulse" aria-hidden="true">
          <span />
          <span />
          <span />
        </div>
        <h1 className="service-screen__title">{copy.title}</h1>
        {copy.body.map((line) => (
          <p className="service-screen__body" key={line}>
            {line}
          </p>
        ))}
        <button
          type="button"
          className="service-screen__retry"
          data-testid="service-recheck"
          onClick={onRecheck}
          // The guard is in the hook as well - a ref, so two clicks in the same
          // frame cannot both start a probe - and this is what says so on
          // screen instead of leaving the button looking ignored.
          disabled={checking}
        >
          {checking ? 'VERIFICANDO…' : 'VERIFICAR NOVAMENTE'}
        </button>
        <p className="service-screen__status" data-testid="service-status">
          {checking
            ? 'Verificando servidor…'
            : checkedAt
              ? `Última verificação às ${clockLabel(checkedAt)}`
              : 'Aguardando a primeira verificação…'}
        </p>
      </div>
    </section>
  );
}
